import { FormGroup, InputGroup } from "@blueprintjs/core";
import { FieldLabel } from "../../CoreFields/FieldLabel/FieldLabel";
import {
  AddressFieldValue,
  SimpleAddressProps,
  FieldLabeledProps,
} from "../../types/fieldTypes";
import TextField from "../../BaseInputFields/TextField/TextField";
import { usStateOptions } from "./defaults";
import { useState } from "react";
import { EnumSingleSelectFieldV2 } from "../../DropdownFields/EnumSingleSelectFieldV2/EnumSingleSelectFieldV2";
import { EnumSingleSelectField } from "../../DropdownFields/EnumSingleSelectField/EnumSingleSelectField";
import { FormFieldErrorContainer } from "../../CoreFields/FieldError/FormFieldErrorContainer";
import { getErrorMessagesFromErrorMessages } from "../../../../utils/field";

export interface InputLabelProps {
  labelForStreetInput?: string;
  labelForStreet2Input?: string;
  labelForCityInput?: string;
  labelForStateInput?: string;
  labelForZipCodeInput?: string;
  labelForCountyInput?: string;
}

export interface SimpleAddressFieldProps extends SimpleAddressProps {
  shouldShowCounty?: boolean;
  labelProps?: FieldLabeledProps;
  inputLabelProps?: InputLabelProps;
  useV2EnumSelect?: boolean;
}

export const SimpleAddressField: React.FC<SimpleAddressFieldProps> = (
  props
) => {
  const [localAddress, setLocalAddress] = useState<
    AddressFieldValue | undefined
  >(props.value);

  const onAddressChangeHandler = (address: AddressFieldValue) => {
    setLocalAddress(address);
    props.onSubmit && props.onSubmit(address);
  };

  const streetErrorMessages = getErrorMessagesFromErrorMessages(
    props.errorMessages,
    "Street"
  );

  return (
    <FormGroup>
      {props.label && (
        <FieldLabel
          labelName={props.label}
          tooltipDescription={props.description}
          fieldProps={{ ...props, value: localAddress }}
          isRequired={props.isRequired}
          {...props.labelProps}
        />
      )}
      <div className="address-field_input address-street">
        {props?.inputLabelProps?.labelForStreetInput && (
          <FieldLabel
            labelName={props?.inputLabelProps?.labelForStreetInput}
            tooltipDescription={props.description}
            fieldProps={{ ...props, value: localAddress }}
            isRequired={props.isRequired}
            {...props.labelProps}
          />
        )}
        <InputGroup
          autoComplete="street-address"
          className={`address_street_field ${
            props.isRequired && !localAddress?.street
              ? "has-required-background"
              : ""
          }`}
          value={localAddress?.street}
          disabled={props.disabled}
          placeholder={"Street 1"}
          onChange={(e) => {
            if (e.target.value !== localAddress?.street) {
              const street = e.target.value;
              onAddressChangeHandler({ ...localAddress, street });
            }
          }}
        />
        {streetErrorMessages && (
          <FormFieldErrorContainer errorMessages={streetErrorMessages} />
        )}
      </div>
      <div className="address-field_input additional-street">
        <TextField
          type="Text"
          label={props?.inputLabelProps?.labelForStreet2Input ?? ""}
          placeholder="Street 2"
          value={localAddress?.street2}
          errorMessages={getErrorMessagesFromErrorMessages(
            props.errorMessages,
            "Street2"
          )}
          inputMode="text"
          disabled={props.disabled}
          onSubmit={(value) => {
            if (value !== localAddress?.street2) {
              onAddressChangeHandler({
                ...localAddress,
                street2: value,
              });
            }
          }}
        />
      </div>
      <div className="flex-field">
        <div className="address-field_input">
          <TextField
            type="Text"
            label={props?.inputLabelProps?.labelForCityInput ?? ""}
            placeholder="City"
            value={localAddress?.city}
            errorMessages={getErrorMessagesFromErrorMessages(
              props.errorMessages,
              "City"
            )}
            inputMode="text"
            disabled={props.disabled}
            width="20%"
            onSubmit={(value) => {
              if (value !== localAddress?.city)
                onAddressChangeHandler({
                  ...localAddress,
                  city: value,
                });
            }}
            isRequired={props.isRequired}
          />
        </div>
        <div className="address-field_input">
          {props?.useV2EnumSelect ? (
            <EnumSingleSelectFieldV2
              type="EnumSingleSelect"
              label={props?.inputLabelProps?.labelForStateInput ?? ""}
              placeholder="State"
              value={localAddress?.state}
              errorMessages={getErrorMessagesFromErrorMessages(
                props.errorMessages,
                "State"
              )}
              disabled={props.disabled}
              isRequired={props.isRequired}
              optionValues={usStateOptions}
              onSubmit={(value) => {
                if (value !== localAddress?.state) {
                  onAddressChangeHandler({
                    ...localAddress,
                    state: value as number,
                  });
                }
              }}
            />
          ) : (
            <EnumSingleSelectField
              type="EnumSingleSelect"
              label={props?.inputLabelProps?.labelForStateInput ?? ""}
              placeholder="State"
              value={localAddress?.state}
              errorMessages={getErrorMessagesFromErrorMessages(
                props.errorMessages,
                "State"
              )}
              disabled={props.disabled}
              isRequired={props.isRequired}
              optionValues={usStateOptions}
              onSubmit={(value) => {
                if (value !== localAddress?.state) {
                  onAddressChangeHandler({
                    ...localAddress,
                    state: value as number,
                  });
                }
              }}
            />
          )}
        </div>
        <div className="address-field_input">
          <TextField
            type="Text"
            label={props?.inputLabelProps?.labelForZipCodeInput ?? ""}
            value={localAddress?.zip}
            errorMessages={getErrorMessagesFromErrorMessages(
              props.errorMessages,
              "Zip"
            )}
            placeholder={"Zip"}
            inputMode="text"
            disabled={props.disabled}
            onSubmit={(value) => {
              if (value !== localAddress?.zip) {
                onAddressChangeHandler({
                  ...localAddress,
                  zip: value,
                });
              }
            }}
            isRequired={props.isRequired}
          />
        </div>
        {props.shouldShowCounty && (
          <div className="address-field_input">
            <TextField
              type="Text"
              label={props?.inputLabelProps?.labelForCountyInput ?? ""}
              value={localAddress?.county}
              errorMessages={getErrorMessagesFromErrorMessages(
                props.errorMessages,
                "County"
              )}
              placeholder={"County"}
              inputMode="text"
              disabled={props.disabled}
              onSubmit={(value) => {
                if (value !== localAddress?.county) {
                  onAddressChangeHandler({
                    ...localAddress,
                    county: value,
                  });
                }
              }}
            />
          </div>
        )}
      </div>
      {props.errorMessages && (
        <>
          {props.errorMessages
            .filter((e) => typeof e === "string")
            .map((errorMessage, idx) => (
              <p key={idx} className="error-message">
                {errorMessage}
              </p>
            ))}
        </>
      )}
    </FormGroup>
  );
};
